/**
 * Error Page - Handles various error states (404, 403, 500, etc.)
 */

import { Link } from 'react-router-dom';
import { Home, RefreshCw, AlertTriangle } from 'lucide-react';

interface ErrorPageProps {
  code?: number | string;
  title?: string;
  message?: string;
  showRefresh?: boolean;
}

const errorDefaults: Record<number, { title: string; message: string }> = {
  404: {
    title: 'Page not found',
    message: "The page you're looking for doesn't exist or has been moved.",
  },
  403: {
    title: 'Access denied',
    message: "You don't have permission to access this page.",
  },
  500: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again later.',
  },
};

export function ErrorPage({
  code = 404,
  title,
  message,
  showRefresh = false,
}: ErrorPageProps) {
  const defaults = errorDefaults[Number(code)] || errorDefaults[500];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-6">
          <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-6xl font-bold text-gray-900 dark:text-white">{code}</h1>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mt-4">
          {title || defaults.title}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-md">
          {message || defaults.message}
        </p>
        <div className="flex items-center justify-center gap-4 mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </Link>
          {showRefresh && (
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-xl transition-all"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Page
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
